import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

export const useBeneficiarySelectionLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'Beneficiary Selection',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_BENEFICIARY_SELECTION]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_BENEFICIARY_SELECTION) || '',
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_BENEFICIARY_SELECTION]: z.string().superRefine((val, ctx) => {
        if (!val) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.required'),
          });
          return;
        }
        if (val.length < 30) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.min30chars'),
          });
        } else if (val.length > 300) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.max300chars'),
          });
        }
      }),
    }),
  });

  const handleSubmit = async () => {
    saveEddQuestionToLocalStore({
      question: t('organic.beneficiarySelection.content'),
      questionId: EddEmbeddedFields.EDD_BENEFICIARY_SELECTION,
      answer: JSON.stringify(formMethods.getValues(EddEmbeddedFields.EDD_BENEFICIARY_SELECTION)),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_BENEFICIARY_SELECTION);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
